'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React, {
  useImperativeHandle,
  useState,
  createContext,
  useContext,
  useMemo
} from 'react'
import moment from 'moment'
import { noop } from 'lodash'
import { Portal } from '@gorhom/portal'

// lib
import Box, { BoxProps } from '../box'
import Button from '../button'
import { ChevronLeftIcon, ChevronRightIcon } from '../icons'
import LinearGradient from '../linear-gradient'
import Pressable from '../pressable'
import Text from '../text'

export const DATE_TIME_PICKER_MAX_HEIGHT = 406
const DATE_TIME_PICKER_MAX_WIDTH = 328
const ROW_HEIGHT = 44
const DOT_SIZE = 6
// the picked date keeps the current time, rounded down to this many minutes
const TIME_INTERVAL = 15

/* -----------------------------------------------------------------------------
 * DatePickerCalendarDay
 * -------------------------------------------------------------------------- */

type DatePickerCalendarDayProps = {
  day: moment.Moment
}

const DatePickerCalendarDay = ({ day }: DatePickerCalendarDayProps) => {
  const { displayDate, selectedDate, setSelectedDate, minDate, maxDate } =
    useContext(DateTimePickerContext)
  const isToday = day.isSame(moment(), 'day')
  const isThisMonth = day.isSame(displayDate, 'month')
  const isSelected = day.isSame(selectedDate, 'day')
  const isDisabled =
    (minDate && day.isBefore(minDate, 'day')) ||
    (maxDate && day.isAfter(maxDate, 'day'))

  const selectDate = () => {
    const dateStr = day.format('YYYY-MM-DD')
    const timeStr = selectedDate.format('HH:mm:ss')
    setSelectedDate(moment(`${dateStr}T${timeStr}`, 'YYYY-MM-DDTHH:mm:ss'))
  }

  return (
    <Pressable onPress={isDisabled ? noop : selectDate}>
      <Box
        justifyContent='center'
        alignItems='center'
        width={ROW_HEIGHT}
        height={ROW_HEIGHT}
        borderRadius='small'
        borderColor='violet'
        borderWidth={isSelected ? 1 : 0}
      >
        {/* This is spacing to ensure the calendar number remains level with the other numbers */}
        {isToday ? (
          <Box width={DOT_SIZE} height={DOT_SIZE} bg='transparent' />
        ) : null}
        <Text
          variant='small'
          textColor={isThisMonth && !isDisabled ? 'purple' : 'shade04'}
        >
          {day.format('D')}
        </Text>
        {isToday ? (
          <LinearGradient
            width={DOT_SIZE}
            height={DOT_SIZE}
            variant='violetAngledGradient'
            borderRadius='pill'
          />
        ) : null}
      </Box>
    </Pressable>
  )
}

/* -----------------------------------------------------------------------------
 * DatePickerCalendarRow
 * -------------------------------------------------------------------------- */

type DatePickerCalendarRowProps = {
  weeksAfterFirst: number
  startOfMonth: moment.Moment
  startOfMonthDayOfWeek: number
}

const DatePickerCalendarRow = ({
  weeksAfterFirst,
  startOfMonth,
  startOfMonthDayOfWeek
}: DatePickerCalendarRowProps) => {
  const week = useMemo(() => {
    let thisWeek: moment.Moment[] = []
    let startOfWeek = moment(startOfMonth)
      .add(weeksAfterFirst, 'week')
      .subtract(startOfMonthDayOfWeek, 'days')

    for (let i = 0; i < 7; i += 1) {
      thisWeek.push(startOfWeek)
      startOfWeek = moment(startOfWeek).add(1, 'day')
    }

    return thisWeek
  }, [weeksAfterFirst, startOfMonth, startOfMonthDayOfWeek])

  return (
    <Box flexDirection='row' justifyContent='space-between'>
      {week.map(day => (
        <DatePickerCalendarDay key={day.toISOString()} day={day} />
      ))}
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * DatePickerCalendar
 * -------------------------------------------------------------------------- */

const DatePickerCalendar = () => {
  const { displayDate } = useContext(DateTimePickerContext)
  const startOfMonth = moment(displayDate).startOf('month')
  const startOfMonthDayOfWeek = startOfMonth.day()
  const calendarRowIndices = [0, 1, 2, 3, 4]

  return (
    <>
      {calendarRowIndices.map(weeksAfterFirst => (
        <DatePickerCalendarRow
          key={weeksAfterFirst}
          weeksAfterFirst={weeksAfterFirst}
          startOfMonth={startOfMonth}
          startOfMonthDayOfWeek={startOfMonthDayOfWeek}
        />
      ))}
    </>
  )
}

/* -----------------------------------------------------------------------------
 * DatePickerDaysOfWeek
 * -------------------------------------------------------------------------- */

const DatePickerDaysOfWeek = () => {
  return (
    <Box
      borderColor='shade02'
      borderBottomWidth={1}
      px='20'
      py='12'
      flexDirection='row'
      justifyContent='space-between'
      alignItems='center'
    >
      <Text textColor='shade06'>Su</Text>
      <Text textColor='shade06'>Mo</Text>
      <Text textColor='shade06'>Tu</Text>
      <Text textColor='shade06'>We</Text>
      <Text textColor='shade06'>Th</Text>
      <Text textColor='shade06'>Fr</Text>
      <Text textColor='shade06'>Sa</Text>
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * DatePicker
 * -------------------------------------------------------------------------- */

const DatePicker = () => {
  const { displayDate, setDisplayDate } = useContext(DateTimePickerContext)

  const goToPrevMonth = () => {
    setDisplayDate(moment(displayDate).subtract(1, 'months'))
  }

  const goToNextMonth = () => {
    setDisplayDate(moment(displayDate).add(1, 'months'))
  }

  return (
    <Box width={328} borderRightWidth={1} borderColor='shade02'>
      <Box
        borderColor='shade02'
        borderBottomWidth={1}
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        py='16'
        px='24'
      >
        <Text variant='subtitle'>{displayDate.format('MMMM YYYY')}</Text>
        <Box flexDirection='row' alignItems='center'>
          <Pressable onPress={goToPrevMonth}>
            <ChevronLeftIcon mr='24' />
          </Pressable>
          <Pressable onPress={goToNextMonth}>
            <ChevronRightIcon />
          </Pressable>
        </Box>
      </Box>
      <DatePickerDaysOfWeek />
      <DatePickerCalendar />
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * PickerFooter
 * -------------------------------------------------------------------------- */

type PickerFooterProps = {
  showSaveButton?: boolean
}

const PickerFooter = ({ showSaveButton = true }: PickerFooterProps) => {
  const { dismissPicker, resetPicker, onSubmit, selectedDate } = useContext(
    DateTimePickerContext
  )

  const handleCancel = () => {
    dismissPicker()
    resetPicker()
  }

  const handleSave = () => {
    onSubmit(selectedDate)
    dismissPicker()
    resetPicker()
  }

  return (
    <Box
      borderTopWidth={1}
      borderColor='shade02'
      flexDirection='row'
      justifyContent='flex-end'
      alignItems='center'
      py='16'
      pr='24'
    >
      <Pressable onPress={handleCancel}>
        <Text textColor='violet' font='text.12.semibold'>
          Cancel
        </Text>
      </Pressable>
      {showSaveButton && (
        <Button size='small' px='16' ml='24' onPress={handleSave}>
          Save
        </Button>
      )}
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * DateTimePicker
 * -------------------------------------------------------------------------- */

export interface DateTimePickerContext
  extends Required<Pick<DateTimePickerProps, 'onSubmit'>> {
  displayDate: moment.Moment
  selectedDate: moment.Moment
  minDate?: moment.Moment
  maxDate?: moment.Moment
  dismissPicker: () => void
  resetPicker: () => void
  setDisplayDate: React.Dispatch<React.SetStateAction<moment.Moment>>
  setSelectedDate: (value: moment.Moment) => void
}

export const DateTimePickerContext = createContext<DateTimePickerContext>(null)

export type DateTimePickerProps = BoxProps & {
  onSubmit: (value: moment.Moment) => void
  onChange?: (value: moment.Moment) => void
  dismissOnOverlayClick?: boolean
  resetOnDismiss?: boolean
  minDate?: moment.Moment
  maxDate?: moment.Moment
  submitOnDateChange?: boolean
  hostName?: string | null
}

export type ImperativeDateTimePickerProps = {
  dismiss: () => void
  present: () => void
  reset: () => void
}

const DateTimePicker = React.forwardRef<
  ImperativeDateTimePickerProps,
  DateTimePickerProps
>(
  (
    {
      onChange,
      onSubmit,
      minDate,
      maxDate,
      dismissOnOverlayClick = true,
      resetOnDismiss = false,
      submitOnDateChange = false,
      hostName = null,
      ...rest
    },
    ref
  ) => {
    const now = moment()
    const intervalRemainder = now.minute() % TIME_INTERVAL
    const initialDate = now
      .startOf('minute')
      .subtract(intervalRemainder, 'minutes')

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [selectedDate, setSelectedDate] = useState<moment.Moment>(initialDate)
    const [displayDate, setDisplayDate] = useState<moment.Moment>(initialDate)

    const dismissPicker = () => {
      setIsOpen(false)
      if (resetOnDismiss) {
        resetPicker()
      }
    }

    const presentPicker = () => {
      setIsOpen(true)
    }

    const resetPicker = () => {
      setSelectedDate(initialDate)
    }

    const updateSelectedDate = (value: moment.Moment) => {
      if (submitOnDateChange) {
        onSubmit(value)
        return dismissPicker()
      }

      setSelectedDate(value)

      if (onChange) {
        onChange(value)
      }
    }

    const handleSubmit = () => {
      onSubmit(selectedDate)
      dismissPicker()
    }

    useImperativeHandle(
      ref,
      () => ({
        dismiss: dismissPicker,
        present: presentPicker,
        reset: resetPicker
      }),
      [ref]
    )

    return (
      <DateTimePickerContext.Provider
        value={{
          displayDate,
          selectedDate,
          minDate,
          maxDate,
          dismissPicker,
          resetPicker,
          setDisplayDate,
          setSelectedDate: updateSelectedDate,
          onSubmit: handleSubmit
        }}
      >
        {isOpen ? (
          <React.Fragment>
            {dismissOnOverlayClick && (
              <Portal hostName={hostName !== null ? hostName : 'root'}>
                <Pressable
                  onPress={dismissPicker}
                  position='absolute'
                  left={0}
                  top={0}
                  width='100%'
                  height='100%'
                  zIndex={1}
                  style={
                    dismissOnOverlayClick
                      ? { cursor: 'pointer' }
                      : ({ cursor: 'default' } as any)
                  }
                />
              </Portal>
            )}

            <Box
              borderRadius='small'
              boxShadow='medium'
              flexDirection='column'
              maxHeight={DATE_TIME_PICKER_MAX_HEIGHT}
              maxWidth={DATE_TIME_PICKER_MAX_WIDTH}
              bg='white'
              position='absolute'
              zIndex={2}
              {...rest}
            >
              <Box flexDirection='row'>
                <DatePicker />
              </Box>
              <PickerFooter showSaveButton={!submitOnDateChange} />
            </Box>
          </React.Fragment>
        ) : null}
      </DateTimePickerContext.Provider>
    )
  }
)

export default DateTimePicker
